import { Menu, Bell } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../shared/ui/Logo";
import { Button } from "../ui/button";
import { ThemeToggle } from "../shared/ThemeToggle";
import { cn } from "@/lib/utils";

interface MobileHeaderProps {
  onMenuToggle: () => void;
  isMenuOpen: boolean;
  /**
   * OPCIONAL: si se provee, la campana abre el panel de notificaciones (igual
   * que en escritorio) en vez de navegar a /alerts. Sin ella se mantiene el
   * comportamiento anterior.
   */
  onNotificationsToggle?: () => void;
  /** Intención de abrir el panel (toque/foco): permite precargar su chunk. */
  onNotificationsIntent?: () => void;
  /** Estado del panel de notificaciones, para reflejarlo en la campana. */
  showNotifications?: boolean;
}

export function MobileHeader({
  onMenuToggle,
  isMenuOpen,
  onNotificationsToggle,
  onNotificationsIntent,
  showNotifications = false,
}: MobileHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const alertsActive = location.pathname === "/alerts";
  // Sin panel disponible, la campana sigue llevando a la página de alertas.
  const bellActive = onNotificationsToggle ? showNotifications : alertsActive;

  return (
    // Sin `sticky`: la cabecera es una fila fija del shell (su padre recorta el
    // overflow y NO desplaza), así que `sticky top-0` no hacía nada y sugería
    // un comportamiento de scroll que no existe.
    // El relleno lateral SUMA la safe-area: en horizontal el notch se comía el
    // botón de menú. (`px-safe` no sirve aquí: sustituiría al relleno base.)
    <header className="z-nav flex min-h-16 shrink-0 items-center justify-between gap-2 border-b border-border bg-background/90 pb-2 pl-[calc(0.75rem+env(safe-area-inset-left,0px))] pr-[calc(0.75rem+env(safe-area-inset-right,0px))] pt-safe-header shadow-sm backdrop-blur-xl md:hidden">
      <div className="flex min-w-0 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuToggle}
          aria-label="Abrir menú"
          aria-controls="app-sidebar"
          aria-expanded={isMenuOpen}
          // 44px en toda la app: el tamaño mínimo de un objetivo táctil.
          className="h-11 w-11 shrink-0 touch-manipulation sm:h-11 sm:w-11"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <Logo className="h-9 w-9 shrink-0" />
          <span className="truncate text-sm font-semibold text-foreground">Agricos Monitor</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <ThemeToggle />
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            onNotificationsToggle ? onNotificationsToggle() : navigate("/alerts")
          }
          onTouchStart={onNotificationsIntent}
          onFocus={onNotificationsIntent}
          aria-label="Notificaciones"
          aria-expanded={onNotificationsToggle ? showNotifications : undefined}
          aria-haspopup={onNotificationsToggle ? "dialog" : undefined}
          className={cn(
            "h-11 w-11 touch-manipulation sm:h-11 sm:w-11",
            bellActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
          )}
        >
          <Bell className={cn("h-5 w-5", bellActive ? "text-primary" : "text-warning")} />
        </Button>
      </div>
    </header>
  );
}
